import styled from '@emotion/styled';
import { Box, Accordion, useDisclosure } from '@chakra-ui/react';

import useItemCreationWizard from 'hooks/useItemCreationWizard';

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { Formik, Form } from 'formik';
import { useIntl } from 'react-intl';

import { getSubRenewables, createRenewableDefinition, deleteSubRenewable } from '../../../../services/renewables';
import { flatItemDescriptions } from '../../../../services/items';
import { onHasUnsavedChanges } from '../../../../store/helpers/helpersSlice';
import { isSubmitDisabled } from '../../../../services/utils';
import { DEFAULTFLATWEIGHT } from 'constants/renewables';

import useCommonToast from '../../../../hooks/useCommonToast';
import ExpandableItemWrapper from '../../../layout/ExpandableItemWrapper';
import AutomaticFormikDirtyCheck from '../../../forms/AutomaticFormikDirtyCheck';
import Error from '../../../utils/Error';
import DefinitionsSkeleton from '../../../utils/DefinitionsSkeleton';
import DeleteRecordModal from '../../../utils/DeleteRecordModal';
import PromptWrapper from '../../../utils/PromptWrapper';
import RenewableDefinition from './RenewableDefinition';
import NavigationButtons from '../NavigationButtons';
import StartNewSubItem from './StartNewSubItem';

const RENEWABLES_ITEM_TYPE = 'renewables';

const RenewablesCustomSubItemStep = () => {
    const {
        bookId: selectedBookId,
        itemTypeId: selectedItemTypeId,
        newItemId,
        projectOnOffDates,
        moveToSubItemDecisionStep,
        moveToItemPageStep,
        createRenewableProjectDate,
        deleteRenewableProjectDate,
    } = useItemCreationWizard();

    const selectedRenewableRef = useRef(null);

    const intl = useIntl();
    const { toast } = useCommonToast();
    const dispatch = useDispatch();

    const itemType = RENEWABLES_ITEM_TYPE;
    const typeLabel = intl.formatMessage({ id: 'renewable_sub_renewable_label' });

    const [selectedRenewable, setSelectedRenewable] = useState(null);
    const [renewableDefinitions, setRenewableDefinitions] = useState(null);
    const [loadingError, setLoadingError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [editDescriptionConfig, setEditDescriptionConfig] = useState(null);
    const [subRenewableForDeletion, setSubRenewableForDeletion] = useState(null);
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [formsErrorStatuses, setFormsErrorStatuses] = useState([]);

    const addSubRenewableModal = useDisclosure();
    const deleteSubRenewableModal = useDisclosure();

    const formLabels = {
        book: intl.formatMessage({ id: 'renewable_book' }),
        pricingPoint: intl.formatMessage({ id: 'common_electric_pricing_point' }),
        rtelecBasis: intl.formatMessage({ id: 'common_rtelec_pricing_point' }),
        weatherZone: intl.formatMessage({ id: 'renewable_definitions_weather_zone' }),
        modelType: intl.formatMessage({ id: 'renewable_definitions_model_type' }),
        dataWeight: intl.formatMessage({ id: 'renewable_definitions_data_weighting' }),
        plantType: intl.formatMessage({ id: 'renewable_definitions_plant_type' }),
        selectPlaceholder: intl.formatMessage({ id: 'common_forms_select_option' }),
        latitude: intl.formatMessage({ id: 'latitude' }),
        longitude: intl.formatMessage({ id: 'longitude' }),
        master: intl.formatMessage({ id: 'common_master' }),
        createWeatherZone: intl.formatMessage({ id: 'item_creation_wizard_weatherZone_heading' }),
        onlineDate: intl.formatMessage({ id: 'common_online_date' }),
        offlineDate: intl.formatMessage({ id: 'common_offline_date' }),
    };

    const initialFormValues = useMemo(() => {
        const definitions = {};
        renewableDefinitions?.renewableDefinitionResponses.forEach((def, idx) => {
            // validate and parse online and offline dates
            const enteredOnlineDate = projectOnOffDates.find((x) => x.subItemId === def.id)?.enteredOnlineDate;
            const onlineDateObj =
                enteredOnlineDate && !isNaN(Date.parse(enteredOnlineDate)) ? new Date(enteredOnlineDate) : null;

            const enteredOfflineDate = projectOnOffDates.find((x) => x.subItemId === def.id)?.enteredOfflineDate;
            const offlineDateObj =
                enteredOfflineDate && !isNaN(Date.parse(enteredOfflineDate)) ? new Date(enteredOfflineDate) : null;

            // Formik considers numbers in fields as an array of objects
            definitions[idx] = {};
            definitions[idx]['renewableId'] = def.id;
            definitions[idx]['description'] = def.description;
            definitions[idx]['weatherZoneId'] = def.weatherZoneId || null;
            definitions[idx]['moduleProcessId'] = def.moduleProcessId || null;
            definitions[idx]['modelWeightId'] = def.modelWeightId || null;
            definitions[idx]['plantTypeId'] = def.plantTypeId || null;
            definitions[idx]['deliverabilityStatusId'] = def.deliverabilityStatusId || null;
            definitions[idx]['onlineDate'] = onlineDateObj;
            definitions[idx]['offlineDate'] = offlineDateObj;
        });

        return {
            description: selectedRenewable?.description,
            bookId: selectedBookId,
            pricingPointId: selectedRenewable?.pricingPointId,
            definitions: definitions,
            securityId: selectedRenewable?.securityId,
        };
    }, [selectedRenewable, renewableDefinitions, selectedBookId, projectOnOffDates]);

    const fetchSubRenewableItem = useCallback(() => {
        return getSubRenewables(newItemId)
            .then((data) => {
                setSelectedRenewable(data.renewableItem);
                selectedRenewableRef.current = data.renewableItem;

                data.renewableDefinitionResponses.forEach((def) => {
                    const projectDates = projectOnOffDates.find((date) => date.id === def.id);
                    if (projectDates) {
                        def.onlineDate = projectDates.enteredOnlineDate;
                        def.offlineDate = projectDates.enteredOfflineDate;
                    }
                });
                setRenewableDefinitions(data);

                const descriptionsConfig = flatItemDescriptions(data.renewableDefinitionResponses, newItemId);
                setEditDescriptionConfig(descriptionsConfig);

                // This is needed to show results after previous error
                setLoadingError(false);
            })
            .catch(() => setLoadingError(true))
            .finally(() => setIsLoading(false));
    }, [newItemId, projectOnOffDates]);

    useEffect(() => {
        setIsLoading(true);
        fetchSubRenewableItem();
    }, [fetchSubRenewableItem]);

    useEffect(() => {
        dispatch(onHasUnsavedChanges(isFormDirty));
    }, [dispatch, isFormDirty]);

    const onRenewableFormSubmit = async () => {
        moveToItemPageStep();
    };

    const onStartSubItemSubmit = async ({
        description,
        weatherZoneId,
        moduleProcessId,
        plantTypeId,
        onlineDate,
        offlineDate,
    }) => {
        const payload = {
            description,
            weatherZoneId,
            moduleProcessId,
            modelWeightId: DEFAULTFLATWEIGHT,
            plantTypeId,
            itemId: newItemId,
            itemTypeId: selectedItemTypeId,
        };

        const successMessage = intl.formatMessage({ id: 'common_sub_item_creation_success' }, { type: typeLabel });

        const tempSubItem = await createRenewableDefinition(payload);

        // store online and offline dates for the new sub item in state
        createRenewableProjectDate(tempSubItem.id, onlineDate, offlineDate);

        tempSubItem.onlineDate = onlineDate;
        tempSubItem.offlineDate = offlineDate;

        onAddSubItem(tempSubItem);
        toast(successMessage);
    };

    const onAddSubItem = (subRenewable) => {
        setEditDescriptionConfig((prevState) => ({
            ...prevState,
            [subRenewable.id]: { isActive: false, penDisabled: false },
        }));
        if (formsErrorStatuses?.length > 0) {
            setFormsErrorStatuses([...formsErrorStatuses, { id: subRenewable.id, hasErrors: false }]);
        } else {
            setFormsErrorStatuses([{ id: subRenewable.id, hasErrors: false }]);
        }

        setRenewableDefinitions((prev) => ({
            ...prev,
            renewableDefinitionResponses: [...prev.renewableDefinitionResponses, subRenewable],
        }));
    };

    const onSubRenewableDeleteModalOpen = (subRenewableId, subRenewableDesc) => {
        setSubRenewableForDeletion({
            id: subRenewableId,
            description: subRenewableDesc,
        });

        deleteSubRenewableModal.onOpen();
    };

    const onSubRenewableDeleteModalClose = () => {
        deleteSubRenewableModal.onClose();
        setSubRenewableForDeletion(null);
    };

    const onSubRenewableDelete = async (subRenewableId) => {
        onSubRenewableDeleteModalClose();

        const successMessage = intl.formatMessage({ id: 'renewable_delete_success' });

        try {
            setIsLoading(true);

            await deleteSubRenewable(subRenewableId);
            deleteRenewableProjectDate(subRenewableId);
            await fetchSubRenewableItem();
            if (formsErrorStatuses.length > 0) {
                setFormsErrorStatuses(formsErrorStatuses.filter((x) => x.id !== subRenewableId));
            }

            toast(successMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const onEditDescription = (subRenewableId) => {
        if (!editDescriptionConfig[subRenewableId].penDisabled) {
            setEditDescriptionConfig((prevState) => {
                return {
                    ...prevState,
                    [subRenewableId]: { isActive: !prevState[subRenewableId].isActive, penDisabled: false },
                };
            });
        }
    };

    const validateDescription = (value, selectedRenewableId = null) => {
        const trimmed = value.trim();
        let errorMessage = '';

        if (trimmed.length === 0) {
            errorMessage = intl.formatMessage(
                { id: 'common_forms_validation_required' },
                { label: 'Sub-installation name' }
            );
        } else if (trimmed.length > 50) {
            errorMessage = intl.formatMessage(
                { id: 'common_forms_validation_length' },
                { label: 'Sub-installation name', lengthRule: '50 characters or less' }
            );
        }

        if (selectedRenewableId !== null) {
            if (errorMessage !== '') {
                setEditDescriptionConfig((prevState) => {
                    return {
                        ...prevState,
                        [selectedRenewableId]: { ...prevState[selectedRenewableId], penDisabled: true },
                    };
                });
            } else {
                setEditDescriptionConfig((prevState) => {
                    return {
                        ...prevState,
                        [selectedRenewableId]: { ...prevState[selectedRenewableId], penDisabled: false },
                    };
                });
            }
        }

        return errorMessage;
    };

    const handlePreviousStep = () => {
        moveToSubItemDecisionStep();
    };

    const updateFormIssueStatuses = useCallback((subRenewableId, hasErrors) => {
        if (formsErrorStatuses.length > 0) {
            const updatedFormsErrorStatuses = formsErrorStatuses.map((x) => {
                if (x.id === subRenewableId) {
                    return { id: subRenewableId, hasErrors: hasErrors };
                }
                return x;
            });

            if (!updatedFormsErrorStatuses.some((x) => x.id === subRenewableId)) {
                updatedFormsErrorStatuses.push({ id: subRenewableId, hasErrors: hasErrors });
            }
            setFormsErrorStatuses(updatedFormsErrorStatuses);
        } else {
            setFormsErrorStatuses([{ id: subRenewableId, hasErrors: hasErrors }]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const anyFormHasErrors = useMemo(() => {
        return formsErrorStatuses?.length > 0 ? formsErrorStatuses.some((x) => x.hasErrors) : false;
    }, [formsErrorStatuses]);

    return (
        <>
            <PromptWrapper />

            <ExpandableItemWrapper>
                {isLoading ? (
                    <CenteredWrapper>
                        <DefinitionsSkeleton />
                    </CenteredWrapper>
                ) : loadingError ? (
                    <Box m={6}>
                        <Error
                            primaryId="common_error"
                            secondaryId="common_loading_error"
                            additionalText="definitions"
                        />
                    </Box>
                ) : (
                    <>
                        <StartNewSubItem
                            formLabels={formLabels}
                            renewableDefinitions={renewableDefinitions}
                            onStartSubItemSubmit={onStartSubItemSubmit}
                            validateDescription={validateDescription}
                        />
                        <Formik
                            enableReinitialize
                            onSubmit={(errors, values) => onRenewableFormSubmit(errors, values)}
                            initialValues={initialFormValues}
                        >
                            {({ isSubmitting, isValidating, isValid, errors, touched, values }) => {
                                const isDisabled = isSubmitDisabled({ errors, touched });
                                return (
                                    <Form>
                                        <>
                                            <AutomaticFormikDirtyCheck
                                                isFormDirty={isFormDirty}
                                                onFormDirtyChange={setIsFormDirty}
                                            />
                                            <CenteredWrapper>
                                                {renewableDefinitions.renewableDefinitionResponses.length > 0 && (
                                                    <Box>
                                                        <Accordion allowToggle defaultIndex={[0]}>
                                                            {renewableDefinitions.renewableDefinitionResponses.map(
                                                                (def, idx) => {
                                                                    const itemId = def.id;
                                                                    return (
                                                                        <RenewableDefinition
                                                                            key={itemId}
                                                                            itemId={itemId}
                                                                            itemTypeId={selectedItemTypeId}
                                                                            type={itemType}
                                                                            idx={idx}
                                                                            definitions={renewableDefinitions}
                                                                            formLabels={formLabels}
                                                                            editDescriptionConfig={
                                                                                editDescriptionConfig
                                                                            }
                                                                            helpPageUrlItemTypeId={selectedItemTypeId}
                                                                            onEditDescription={onEditDescription}
                                                                            setEditDescriptionConfig={
                                                                                setEditDescriptionConfig
                                                                            }
                                                                            onAddSubRenewableModalOpen={
                                                                                addSubRenewableModal.onOpen
                                                                            }
                                                                            onDeleteSubRenewableModalOpen={
                                                                                onSubRenewableDeleteModalOpen
                                                                            }
                                                                            onIssuesChange={updateFormIssueStatuses}
                                                                        />
                                                                    );
                                                                }
                                                            )}
                                                        </Accordion>
                                                    </Box>
                                                )}
                                            </CenteredWrapper>
                                            <NavigationButtons
                                                isPrevStepDisabled={
                                                    isSubmitting ||
                                                    renewableDefinitions?.renewableDefinitionResponses?.length > 0
                                                }
                                                isConfirmSubmitting={isSubmitting}
                                                isConfirmDisabled={
                                                    anyFormHasErrors ||
                                                    isDisabled ||
                                                    renewableDefinitions?.renewableDefinitionResponses?.length <= 0
                                                }
                                                handlePreviousStep={handlePreviousStep}
                                            />
                                        </>
                                    </Form>
                                );
                            }}
                        </Formik>
                        {deleteSubRenewableModal.isOpen && (
                            <DeleteRecordModal
                                onClose={onSubRenewableDeleteModalClose}
                                headerItem={intl.formatMessage({
                                    id: 'renewable_sub_renewable_heading',
                                })}
                                contentMessageItem={subRenewableForDeletion.description}
                                onConfirm={() => onSubRenewableDelete(subRenewableForDeletion.id)}
                            />
                        )}
                        ;
                    </>
                )}
            </ExpandableItemWrapper>
        </>
    );
};

const CenteredWrapper = styled(Box)`
    width: 100%;
    max-width: 1314px;
    margin: 0 auto;
    padding: 0 16px;
`;

export default RenewablesCustomSubItemStep;
